<script setup lang="ts">
import { formatDateTimeLong, formatDateTimeRelative, formatNumber } from '@metapic-apps/shared'
import { computed } from 'vue'

import CampaignTargetsRow from '@/components/CampaignListItem/CampaignTargetsRow.vue'
import CostDataRow from '@/components/CampaignListItem/CostDataRow.vue'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { isFutureDate, round } from '@/lib/helpers'
import { CampaignTypeEnum } from '@/lib/types/campaign.types'
import { type CampaignListItemType, CampaignStatusEnum } from '@/lib/types/campaigns.types'
import {
  BoxComponent,
  FlexComponent,
  ImageComponent,
  ProgressComponent,
  TagComponent,
  TooltipComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{
  item: CampaignListItemType
  isLoading?: boolean
  showTargets?: boolean
  isCompact?: boolean
  hasUpdate?: boolean
  showCreatorMetrics?: boolean
}>()
const emits = defineEmits(['onDelete', 'onOpen'])

const campaignTypesMap = {
  [CampaignTypeEnum.Standard]: 'Without approval (Offer)',
  [CampaignTypeEnum.UserAccepts]: 'Creator accepts',
  [CampaignTypeEnum.StoreAccepts]: 'Advertiser approves creators',
  [CampaignTypeEnum.Suggestion]: 'Suggestions',
}

const campaignStatusMap = {
  [CampaignStatusEnum.Active]: 'Active',
  [CampaignStatusEnum.Paused]: 'Paused',
  [CampaignStatusEnum.Scheduled]: 'Scheduled',
  [CampaignStatusEnum.Ended]: 'Ended',
}

const shouldShowDate = computed(() => Boolean(isFutureDate(props.item.validFrom || '') || isFutureDate(props.item.validUntil || '')))

const dateLabel = (dateFrom: string | undefined, dateUntil: string | undefined) => {
  if (dateFrom && isFutureDate(dateFrom)) {
    return `Starts ${formatDateTimeRelative(dateFrom)}`
  }
  if (dateUntil && isFutureDate(dateUntil)) {
    return `Ends ${formatDateTimeRelative(dateUntil)}`
  }
}

const dateFull = (dateFrom: string | undefined, dateUntil: string | undefined) => {
  if (dateFrom && isFutureDate(dateFrom)) {
    return formatDateTimeLong(dateFrom)
  }
  if (dateUntil && isFutureDate(dateUntil)) {
    return formatDateTimeLong(dateUntil)
  }
}

const handleClick = (id: number) => {
  emits('onOpen', id)
}
</script>
<template>
  <BoxComponent
    :pt="24"
    :pr="isCompact ? 0 : 24"
    :pb="24"
    :pl="24"
    :class="['campaign-list-item__container', 'campaign-list-item__cursor-pointer', { 'campaign-list-item__updates-indicator': hasUpdate }]"
    @click="() => handleClick(item.id)"
  >
    <FlexComponent align="center" justify="space-between" :gap="16">
      <BoxComponent w="30%">
        <FlexComponent direction="column" :gap="8">
          <FlexComponent v-if="!isCompact" :gap="8" align="center">
            <ImageComponent
              v-if="item.advertiserLogo"
              :image="item.advertiserLogo"
              :width="64"
              :alt="item.advertiserName"
              :height="32"
              object-fit="contain"
              resize-type="fit"
            />
            <TypographyComponent variant="title-medium">{{ item.advertiserName }}</TypographyComponent>
          </FlexComponent>
          <FlexComponent :gap="8" align="center">
            <TagComponent :extra-class="`campaign-list-item__tag campaign-list-item__tag-${item.status?.toLowerCase()}`">
              {{ campaignStatusMap[item.status] }}
            </TagComponent>
            <TypographyComponent variant="title-large" text-truncate>{{ item.internalName }}</TypographyComponent>
          </FlexComponent>
          <FlexComponent align="center">
            <TypographyComponent
              variant="body-small"
              color="var(--secondary-48)"
              white-space="nowrap"
              class="campaign-list-item__footer-item"
            >
              ID: {{ item.id }}
            </TypographyComponent>
            <TypographyComponent
              variant="body-small"
              color="var(--secondary-48)"
              white-space="nowrap"
              class="campaign-list-item__footer-item"
            >
              {{ campaignTypesMap[item.type] }}
            </TypographyComponent>
            <TooltipComponent v-if="shouldShowDate">
              <template #default>
                <TypographyComponent
                  variant="body-small"
                  color="var(--secondary-48)"
                  white-space="nowrap"
                  class="campaign-list-item__footer-item"
                >
                  {{ dateLabel(item.validFrom, item.validUntil) }}
                </TypographyComponent>
              </template>
              <template #content>
                <TypographyComponent variant="body-small"> {{ dateFull(item.validFrom, item.validUntil) }}</TypographyComponent>
              </template>
            </TooltipComponent>
          </FlexComponent>
        </FlexComponent>
      </BoxComponent>
      <FlexComponent align="center" justify="space-between" flex="1" :gap="8">
        <BoxComponent w="75%">
          <FlexComponent align="center" justify="space-between" direction="column" :gap="4">
            <TypographyComponent variant="label-medium" text-truncate>
              {{ showCreatorMetrics ? 'Clicks' : 'Click limit' }}
            </TypographyComponent>
            <TypographyComponent v-if="item.maxClicks && item.perUserLimit && !showCreatorMetrics" variant="title-large" text-truncate>
              {{ formatNumber(item.maxClicks, 'compact') }} / creator
            </TypographyComponent>
            <ProgressComponent
              v-else-if="item.maxClicks"
              :content="
                item.currentClicks === undefined && !showCreatorMetrics
                  ? '*'
                  : `${formatNumber(item.currentClicks ?? 0, 'compact')}/${formatNumber(item.maxClicks, 'compact')}`
              "
              :tooltip-content="
                item.currentClicks === undefined && !showCreatorMetrics
                  ? undefined
                  : `${formatNumber(item.currentClicks ?? 0)}/${formatNumber(item.maxClicks)}`
              "
              :percentage="item.currentClicks === undefined ? 0 : Math.ceil((item.currentClicks / item.maxClicks) * 100)"
            />
            <TypographyComponent v-else variant="title-large" text-truncate>
              {{ showCreatorMetrics ? formatNumber(item.currentClicks ?? 0, 'compact') : 'No limit' }}
            </TypographyComponent>
          </FlexComponent>
        </BoxComponent>
        <BoxComponent
          :w="showCreatorMetrics ? 'max-content' : '100%'"
          :min-w="showCreatorMetrics ? 180 : undefined"
          :class="{ 'campaign-list-item__creator-commission': showCreatorMetrics }"
        >
          <FlexComponent align="center" justify="space-between" direction="column" :gap="4">
            <TypographyComponent variant="label-medium">Commission</TypographyComponent>
            <CostDataRow
              v-if="item.trafficSourceCosts?.length"
              :currency="item.advertiserCurrency"
              :grouped-traffic-source-costs="item.trafficSourceCosts"
              justify-content="center"
            />
            <TypographyComponent v-else variant="title-large" text-truncate>No commission</TypographyComponent>
          </FlexComponent>
        </BoxComponent>
        <BoxComponent v-if="!showCreatorMetrics" w="75%">
          <FlexComponent align="center" justify="space-between" direction="column" :gap="4">
            <TypographyComponent variant="label-medium">Budget</TypographyComponent>
            <ProgressComponent
              v-if="item.sharedBudget"
              :content="`${formatMoneyForAdmin({ amount: item.consumedBudget?.amount || 0, currency: item.sharedBudget.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}/${formatMoneyForAdmin({ amount: item.budgetLimit?.amount || item.sharedBudget.amount.amount, currency: item.sharedBudget.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}`"
              :tooltip-content="`${formatMoneyForAdmin({ amount: item.consumedBudget?.amount || 0, currency: item.sharedBudget.currency }, { allowZero: true })} / ${formatMoneyForAdmin({ amount: item.budgetLimit?.amount || item.sharedBudget.amount.amount, currency: item.sharedBudget.currency }, { allowZero: true })} used`"
              :percentage="round(((item.consumedBudget?.amount || 0) / item.sharedBudget.amount.amount) * 100, 3)"
              :has-limit="Boolean(item.budgetLimit)"
              :limit="item.budgetLimit && round((item.budgetLimit.amount / item.sharedBudget.amount.amount) * 100, 3)"
              class="campaign-list-item__progress"
            />
            <ProgressComponent
              v-else-if="item.budgetLimit"
              :content="`${formatMoneyForAdmin({ amount: item.consumedBudget?.amount ?? 0, currency: item.budgetLimit.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}/${formatMoneyForAdmin({ amount: item.budgetLimit.amount, currency: item.budgetLimit.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}`"
              :tooltip-content="`${formatMoneyForAdmin({ amount: item.consumedBudget?.amount ?? 0, currency: item.budgetLimit.currency }, { allowZero: true })} / ${formatMoneyForAdmin({ amount: item.budgetLimit.amount, currency: item.budgetLimit.currency }, { allowZero: true })} used`"
              :percentage="item.budgetLimit.amount ? round(((item.consumedBudget?.amount ?? 0) / item.budgetLimit.amount) * 100, 3) : 0"
              class="campaign-list-item__progress"
            />
            <TypographyComponent v-else variant="title-large" text-truncate>No budget</TypographyComponent>
          </FlexComponent>
        </BoxComponent>
        <BoxComponent v-if="showTargets" w="75%">
          <FlexComponent align="center" justify="space-between" direction="column" :gap="4">
            <TypographyComponent variant="label-medium">Target</TypographyComponent>
            <CampaignTargetsRow :targets="item.targets" />
          </FlexComponent>
        </BoxComponent>
      </FlexComponent>
      <div v-if="!isCompact" class="campaign-list-item__campaign-logo-wrapper">
        <ImageComponent
          v-if="item.image"
          :image="item.image"
          :width="156"
          :height="88"
          skip-height
          alt="Campaign image"
          class="campaign-list-item__campaign-logo"
        />
      </div>
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.campaign-list-item__container {
  position: relative;
  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);

  &:hover {
    background: var(--secondary-16);

    .campaign-list-item__actions {
      display: flex;
    }
  }

  &.campaign-list-item__updates-indicator {
    border-radius: 16px;
    border: 1px solid rgba(234, 79, 133, 0.24);

    &:after {
      content: '';
      position: absolute;
      left: 16px;
      top: 16px;
      width: 8px;
      height: 8px;
      border-radius: 16px;
      background: #ea4f85;
    }
  }
}

.campaign-list-item__cursor-pointer {
  cursor: pointer;
}

.campaign-list-item__campaign-logo-wrapper {
  width: 156px;
}

.campaign-list-item__campaign-logo {
  border-radius: 8px;
  height: 88px;
  object-fit: contain;
}

.campaign-list-item__creator-commission {
  flex-shrink: 0;
}

.campaign-list-item__footer-item {
  &:not(:last-child):after {
    content: '|';
    margin: 0 8px;
  }
}

.campaign-list-item__progress {
  width: 96px;
}

:deep(.campaign-list-item__tag) {
  height: 32px;
  color: var(--inverted);

  &.campaign-list-item__tag-scheduled {
    background: #17a2b8;
  }

  &.campaign-list-item__tag-active {
    background: var(--success);
  }

  &.campaign-list-item__tag-paused {
    background: #f3ba00;
  }

  &.campaign-list-item__tag-ended {
    background: var(--alert);
  }
}
</style>
